import { computed, inject, toValue, type InjectionKey } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAppAccess } from '@common/app-access-layer/composables/useAppAccess'
import { useAuth } from '@common/auth-layer/composables/useAuth'
import { useLocation } from '@common/location-layer/composables/useLocation'

import { createExperienceQueryPartition, type ExperienceQueryPartition } from '../utils/create-experience-query-partition'
import type { ExperienceQueryPartitionContext, ExperienceQueryPartitionScope } from '../types/experience-query-partition'

export type { ExperienceQueryPartitionContext, ExperienceQueryPartitionScope } from '../types/experience-query-partition'

export const EXPERIENCE_QUERY_PARTITION_SCOPE_KEY: InjectionKey<ExperienceQueryPartitionScope> = Symbol('experience-query-partition-scope')

const unavailableIdentity = computed(() => false)

/**
 * Returns a trusted query partition only when the host application opts in.
 * Hosts without a provider retain their existing unpartitioned query behavior.
 */
export const useExperienceQueryPartition = (): ExperienceQueryPartitionContext => {
  const scope = inject(EXPERIENCE_QUERY_PARTITION_SCOPE_KEY, undefined)
  if (!scope) {
    return {
      queryPartition: undefined,
      viewerId: undefined,
      resolvedLocationId: undefined,
      authorizationScope: undefined,
      hasQueryIdentity: unavailableIdentity,
    }
  }

  const auth = useAuth()
  const { locationId } = useLocation()
  const { canAccessApp } = useAppAccess()
  const { locale } = useI18n()

  const viewerId = computed(() => {
    const value = auth.store.contactId
    return typeof value === 'string' && value !== '[object Object]' ? value.trim() : ''
  })
  const resolvedLocationId = computed(() => String(toValue(locationId) ?? '').trim())
  const resolvedLocale = computed(() => String(toValue(locale) ?? '').trim())
  const authorizationScope = computed(
    () =>
      scope.authorizationApps
        .filter(app => canAccessApp(app))
        .map(String)
        .sort()
        .join(',') || 'none'
  )
  const queryPartition = computed<ExperienceQueryPartition | undefined>(() => {
    if (!viewerId.value || !resolvedLocationId.value || !resolvedLocale.value || !authorizationScope.value) return undefined

    return createExperienceQueryPartition({
      locationId: resolvedLocationId.value,
      viewerId: viewerId.value,
      locale: resolvedLocale.value,
      authorizationScope: authorizationScope.value,
    })
  })
  const hasQueryIdentity = computed(() => queryPartition.value !== undefined)

  return {
    queryPartition,
    viewerId,
    resolvedLocationId,
    authorizationScope,
    hasQueryIdentity,
  }
}
